import { useEffect, useState } from 'react'
import { pruefePasswort } from '@raporto/shared'
import { Passwortfeld } from '../ui/Passwortfeld.js'
import type { SessionUser } from '@raporto/shared'
import {
  PAKETE,
  preisText,
  STEUERHINWEIS,
  INDUSTRIES,
  INDUSTRY_LABELS,
  paketVon,
  type Industry
} from '@raporto/shared'
import { Marke } from './Marke.js'
import { api, type Einladungsansicht, type ZefixTreffer } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'
import { OrtsFeldPaar } from '../ui/OrtsFelder.js'
import { ZAHLUNGSZUSAGE } from './Zahlung.js'

/**
 * Ein Betrieb bestellt sich Raporto.
 *
 * Der Bildschirm, der bis hierher gefehlt hat – und ohne den Raporto keine
 * Plattform ist, sondern eine Installation. Ein Betrieb kam bisher nur
 * hinein, weil jemand bei uns ein Skript ausführte.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Drei Schritte, und keiner mehr
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Paket · Betrieb · Zugang. Mehr fragt dieses Formular nicht. Alles Weitere –
 * Logo, Bankverbindung, Mitarbeiter, Preise – kommt danach, in der Software,
 * wo man es sieht und sofort prüfen kann.
 *
 * Die Versuchung ist die umgekehrte: alles gleich hier abfragen, damit der
 * Betrieb «fertig eingerichtet» ist. Das Ergebnis wäre ein Formular mit
 * zwanzig Feldern vor der ersten Minute Nutzen – und die IBAN hat sowieso
 * niemand im Kopf, der gerade eine Software anschaut.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum die Firmensuche zuerst kommt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Weil Name, Adresse und UID zusammen sechs Felder sind, die jeder falsch
 * tippt – besonders die UID. Das Handelsregister weiss es besser und weiss es
 * amtlich. Steht die Suche nicht zur Verfügung, erscheinen die Felder; das
 * Formular funktioniert dann genauso, nur langsamer.
 */

type Schritt = 'paket' | 'betrieb' | 'zugang' | 'zahlung'

/*
  Die Zusage steht in `Zahlung.tsx` und wird hier nur gelesen.

  Zwei Listen mit denselben Sätzen wären zwei Listen, die auseinanderlaufen –
  und die eine, die jemand vergisst, ist die, die der Kunde vor dem Bezahlen
  sieht. Deshalb genau eine Quelle, und beide Seiten zeigen dasselbe.
*/

/*
  Vor der Anmeldung gibt es keinen eigenen Adressbestand – es gibt noch
  keinen Betrieb. Eine feste leere Liste statt einer neuen bei jedem Tippen:
  sonst rechnet die Vorschlagsliste bei jedem Zeichen von vorne.
*/
const OHNE_BESTAND: ReadonlyArray<[string, string]> = []

const LEER = { name: '', street: '', zip: '', city: '', uid: '', email: '', phone: '' }

export function Bestellung({
  paketWunsch,
  einladung,
  aufAngemeldet,
  aufZahlung,
  aufStart,
  aufLogin
}: {
  /** Welches Paket auf der Preisseite angeklickt wurde. */
  paketWunsch: string
  /**
   * Der Schlüssel aus einer Einladung in eine Kita-Gemeinschaft.
   *
   * Er kommt aus der Adresse (`/bestellen?einladung=…`) und wandert
   * unverändert in die Bestellung. Die Anmeldung selbst ist dieselbe wie
   * immer – dasselbe Formular, dieselben Pakete, derselbe Preis. Nur gehört
   * der Betrieb danach zur Gemeinschaft.
   */
  einladung?: string
  aufAngemeldet: (s: SessionUser, paket: string) => void
  /** Weiter zur eingebetteten Kasse – mit dem Schlüssel dieser einen Sitzung. */
  aufZahlung: (kasse: {
    clientSecret: string
    betrieb: string
    publicKey: string
    sandbox: boolean
  }) => void
  aufStart: () => void
  aufLogin: () => void
}): JSX.Element {
  const [schritt, setSchritt] = useState<Schritt>('paket')
  const [paket, setPaket] = useState(paketVon(paketWunsch)?.id ?? 'pro')
  const [branche, setBranche] = useState<Industry>('andere')

  const [firma, setFirma] = useState(LEER)

  /*
    Der Nummernkreis bei der Einrichtung.

    Zugeklappt, weil die Mehrheit bei 1 anfängt und die Frage dann nur im Weg
    steht. Aber sichtbar, weil sie für den Zuzüger die wichtigste Frage des
    ganzen Wegs ist: wer in seiner alten Software zuletzt die 847 geschrieben
    hat und hier bei 1 beginnt, hat doppelte Rechnungsnummern – und die
    bekommt niemand mehr auseinander, weil eine vergebene Nummer nie
    wiederverwendet wird.
  */
  const [umzug, setUmzug] = useState(false)
  const [weiterRechnung, setWeiterRechnung] = useState(1)
  const [weiterOfferte, setWeiterOfferte] = useState(1)
  const [jaehrlich, setJaehrlich] = useState(true)
  const [suchtext, setSuchtext] = useState('')
  const [treffer, setTreffer] = useState<ZefixTreffer[] | null>(null)
  const [sucht, setSucht] = useState(false)
  const [handelsregister, setHandelsregister] = useState(false)
  const [testTage, setTestTage] = useState(30)
  /*
    Der Einladungscode.

    `codeNoetig` kommt vom Server; der Code selbst nie. Die Frage steht im
    ersten Schritt und nicht im letzten: wer keinen hat, soll es erfahren,
    bevor er drei Bildschirme ausgefüllt hat.
  */
  const [codeNoetig, setCodeNoetig] = useState(false)
  const [code, setCode] = useState('')
  /*
    Monatlich oder jährlich – die Frage gehört an den Schluss und nicht zur
    Paketwahl. Wer noch nicht weiss, ob er das Paket will, entscheidet nicht
    über die Laufzeit; wer bis hierher gekommen ist, entscheidet sie in zwei
    Sekunden.
  */
  const [intervall, setIntervall] = useState<'monat' | 'jahr'>('monat')
  /* Läuft die Einrichtung über die Zahlung – und ist das der Testmodus? */
  const [zahlung, setZahlung] = useState({ verfuegbar: false, sandbox: false, publicKey: '' })
  /* Die Preise aus dem Stripe-Konto, nach «paket:intervall». */
  const [preise, setPreise] = useState<Map<string, number | null>>(new Map())
  /* Für welche Branchen ein Startkatalog bereitliegt – sagt der Server. */
  const [mitKatalog, setMitKatalog] = useState<Set<string>>(new Set())

  const [person, setPerson] = useState({ name: '', email: '', passwort: '', phone: '' })
  /*
    Die Kita, die eingeladen hat.

    Steht sie da, ist dieses Formular keine Bestellung von der Strasse mehr,
    sondern die zweite Hälfte einer Einladung – und der Server kennt den
    gemeinsamen Briefkopf längst. Alles, was er kennt, wird hier eingetragen
    statt abgefragt.
  */
  const [kita, setKita] = useState<Einladungsansicht | null>(null)
  /*
    Solange die Auskunft unterwegs ist, steht im Branchenfeld noch «Andere».

    Das ist der Augenblick, in dem jemand auf «Weiter» drückt und sich den
    Betrieb mit der falschen Branche anlegt – und der Netzweg ist auf einem
    Handy im Zug kein Wimpernschlag. Deshalb hält der erste Schritt an,
    solange eine Einladung vorliegt und noch nicht beantwortet ist.
  */
  const [kitaLaeuft, setKitaLaeuft] = useState(Boolean(einladung))
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /* Welche Felder der Browser selbst gefuellt hat - siehe unten. */
  const [vomBrowser, setVomBrowser] = useState<string[]>([])

  useEffect(() => {
    void api
      .registrierungMoeglichkeiten()
      .then((m) => {
        setHandelsregister(m.handelsregister)
        setTestTage(m.testphaseTage)
        setCodeNoetig(m.codeNoetig)
        setZahlung(m.zahlung)
        setPreise(new Map(m.preise.map((p) => [`${p.paket}:${p.intervall}`, p.betragRp])))
        setMitKatalog(new Set(m.branchen.filter((b) => b.startpaket).map((b) => b.id)))
      })
      .catch(() => undefined)
  }, [])

  /*
    Was die Einladung weiss, wird nicht nochmals gefragt.
    ──────────────────────────────────────────────────────────────────────────

    Bis hierher reiste der Schlüssel durch dieses Formular, ohne dass es ihn
    las. Die Eingeladene tippte Adresse und Kontakt ihrer eigenen Kita ab,
    obwohl der Server beides im gemeinsamen Briefkopf stehen hat. Das war
    kein Minimalismus, sondern eine offene Naht.

    Was übernommen wird und was nicht, folgt einer Linie: GEMEINSAM ist der
    Auftritt — Briefkopf, Adresse, allgemeine Kontaktangaben. EIGEN ist die
    Rechtseinheit — Firmenname, UID, Bankverbindung. Deshalb steht der
    Firmenname nur bei einer gemeinsamen Firma hier, und die UID nie: Sie
    steht in der Dachorganisation nicht, und sie aus der Kita abzuleiten
    hiesse, eine fremde Rechnungssteller-Identität anzunehmen. Dafür gibt es
    die Handelsregistersuche einen Bildschirm weiter.

    Alles Eingetragene bleibt änderbar. Was nicht änderbar ist, steht
    überhaupt nicht in diesem Formular: Kita-Name, Logo und Standortadresse
    der Gemeinschaft gehören der Dachorganisation, und die pflegt nur, wer
    dort berechtigt ist.
  */
  useEffect(() => {
    if (!einladung) return
    let weg = false
    void api
      .dachEinladungAnsehen(einladung)
      .then((a) => {
        if (weg) return
        const e = a.einladung
        setKita(e)
        setBranche('kinderbetreuung')
        setFirma((f) => ({
          ...f,
          name: e.rechtsform === 'gemeinsame_firma' ? e.kita : f.name,
          street: e.street || f.street,
          zip: e.zip || f.zip,
          city: e.ort || f.city,
          email: e.kitaEmail || f.email,
          phone: e.kitaPhone || f.phone
        }))
        setPerson((p) => ({ ...p, name: e.name || p.name, email: e.email || p.email }))
      })
      .finally(() => {
        if (!weg) setKitaLaeuft(false)
      })
      .catch(() => {
        /*
          Abgelaufen, zurückgezogen, falsch abgetippt: Dann bleibt dieses
          Formular die gewöhnliche Bestellung. Eine Fehlermeldung gehört
          nicht hierher — die Einladungsseite hat sie schon gezeigt, und der
          Server weist den Schlüssel beim Abschicken ohnehin ab.
        */
      })
    return () => {
      weg = true
    }
  }, [einladung])

  /*
    Hat der Browser Felder gefuellt?

    Auf diesem Bildschirm ist das folgenreicher als in den Einstellungen. Was
    hier im Mailfeld steht, wird die Anmeldeadresse des neuen Betriebs - sie
    besitzt ihn, sie traegt den zweiten Faktor, und sie laesst sich danach
    nicht mehr im Vorbeigehen tauschen. Eine Adresse, die der Browser aus
    einem anderen Zusammenhang vorschlaegt, gehoert womoeglich jemand anderem.

    autoComplete="off" steht auf allen Feldern. Gegen das Adressbuch von
    Chrome hilft es nicht zuverlaessig - das ist eine Browsereinstellung und
    gehoert dem Benutzer. Was bleibt, ist es zu sagen, bevor er den Knopf
    drueckt.
  */
  useEffect(() => {
    if (schritt !== 'zugang') {
      setVomBrowser([])
      return
    }
    const pruefen = (): void => {
      const treffer: string[] = []
      const felder = Array.from(document.querySelectorAll<HTMLInputElement>('#best-zugang input'))
      for (const feld of felder) {
        try {
          if (feld.matches(':-webkit-autofill')) {
            const label = document.querySelector<HTMLLabelElement>(`label[for="${feld.id}"]`)
            treffer.push(label?.textContent?.trim() ?? feld.id)
          }
        } catch {
          /* Browser ohne diesen Selektor: dann gibt es nichts zu melden. */
        }
      }
      setVomBrowser(treffer)
    }
    pruefen()
    const takt = setInterval(pruefen, 700)
    const ende = setTimeout(() => clearInterval(takt), 6000)
    return () => {
      clearInterval(takt)
      clearTimeout(ende)
    }
  }, [schritt])

  async function suchen(): Promise<void> {
    if (suchtext.trim().length < 3) return
    setSucht(true)
    setFehler(null)
    try {
      const r = await api.registrierungFirma(suchtext.trim())
      setTreffer(r.treffer)
    } catch (e) {
      setFehler(fehlerText(e))
      setTreffer([])
    } finally {
      setSucht(false)
    }
  }

  function uebernehmen(t: ZefixTreffer): void {
    /*
      Name und UID kommen aus dem Register, die Adresse nicht: die Suche
      liefert den Sitz, nicht die Strasse. Sie zu erfinden wäre schlimmer als
      sie zu fragen – also stehen die Felder offen da, mit dem Ort schon drin.
    */
    setFirma({ ...LEER, name: t.name, uid: t.uid, city: t.sitz })
    setTreffer(null)
  }

  /**
   * Was stimmen muss, bevor es weitergeht.
   *
   * Als eigene Funktion, weil jetzt zwei Stellen fragen: der Schritt vor der
   * Zusammenfassung und der Knopf darunter. Zwei Kopien dieser Liste würden
   * auseinanderlaufen, und die eine liesse dann durch, was die andere abweist.
   *
   * Sie gibt den Fehlertext zurück statt ihn zu setzen – so kann der Aufrufer
   * entscheiden, ob er ihn anzeigt oder nur den Weg versperrt.
   */
  function wasFehlt(): string | null {
    if (codeNoetig && code.trim() === '') return 'Ohne Einladungscode geht es zurzeit nicht.'
    if (!firma.name.trim()) return 'Ohne Firmennamen geht es nicht.'
    if (!person.name.trim()) return 'Wie heisst die Person, die sich anmeldet?'
    if (!person.email.trim()) return 'Ohne E-Mail-Adresse gibt es keine Anmeldung.'
    /*
      Die Firmenmail ist freiwillig – sie darf nur nicht falsch sein.

      Wer sie leer lässt, bekommt die persönliche Adresse in den Briefkopf und
      kann das in den Einstellungen trennen. Wer etwas hineinschreibt, das
      keine Adresse ist, würde es erst an der ersten Offerte merken.
    */
    if (firma.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(firma.email.trim())) {
      return 'Die Firmenmailadresse sieht nicht wie eine E-Mail-Adresse aus.'
    }
    /*
      Dieselbe Regel wie am Server – und derselbe Satz. Stünde hier eine
      eigene Prüfung, hiesse es im Formular «mindestens 12 Zeichen» und nach
      dem Absenden etwas anderes.
    */
    const pw = pruefePasswort(person.passwort, {
      email: person.email,
      name: person.name,
      betrieb: firma.name
    })
    if (!pw.ok) return pw.text
    return null
  }

  /** Der gemeinsame Körper beider Wege – ein Formular, zwei Ziele. */
  function bestellungskoerper() {
    return {
      paket,
      branche,
      firma,
      person,
      /*
        Der Einladungsschlüssel, falls dieser Betrieb aus einer
        Kita-Gemeinschaft kommt. Er reist bis zum Abschluss mit und wird dort
        eingelöst – nicht hier, nicht beim Checkout.
      */
      ...(einladung ? { einladung } : {}),
      /* Nur mitschicken, wenn er verlangt wird – sonst steht ein leeres Feld im Protokoll. */
      ...(codeNoetig ? { code: code.trim() } : {}),
      /*
        Nur mitschicken, was er wirklich gesagt hat. Wer die Frage gar nicht
        aufgeklappt hat, bekommt die Vorgaben des Servers – und nicht eine 1,
        die hier erfunden wurde und dort wie eine Angabe aussieht.
      */
      nummernkreis: umzug
        ? {
            naechsteRechnungsnummer: weiterRechnung,
            naechsteOffertnummer: weiterOfferte,
            jaehrlich
          }
        : { jaehrlich }
    }
  }

  /**
   * «Weiter zur Zahlung».
   *
   * Hier entsteht nichts. Der Server legt einen Warteraum an und gibt den
   * Schlüssel für eine eingebettete Kasse zurück; die nächste Raporto-Seite
   * übergibt ihn an Stripe. Der Kunde verlässt raporto.ch nicht.
   *
   * Was danach in Raporto entsteht, entscheidet der Webhook und nicht diese
   * Seite.
   */
  async function zurZahlung(): Promise<void> {
    const fehlt = wasFehlt()
    if (fehlt) return setFehler(fehlt)
    setFehler(null)
    setLaeuft(true)
    try {
      const r = await api.registrierungCheckout(
        { ...bestellungskoerper(), intervall },
        uuidv7()
      )
      /*
        Der Schlüssel geht durch den Zustand der Anwendung weiter und NICHT
        durch die Adresszeile. In der Adresszeile landete er im Verlauf, in
        Lesezeichen und im Referrer der nächsten Seite – für einen Schlüssel,
        der eine Zahlungsmaske öffnet, ist das drei Orte zu viel.
      */
      aufZahlung({
        clientSecret: r.clientSecret,
        betrieb: firma.name.trim(),
        publicKey: zahlung.publicKey,
        sandbox: zahlung.sandbox
      })
    } catch (e) {
      setFehler(fehlerText(e))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Der Weg ohne Zahlung.
   *
   * Er gilt nur, solange der Server sagt, dass die Zahlung nicht eingerichtet
   * ist – in Entwicklung und Abnahme. Steht Stripe, weist der Server ihn ab,
   * und dieser Knopf erscheint gar nicht erst.
   */
  async function bestellen(): Promise<void> {
    const fehlt = wasFehlt()
    if (fehlt) return setFehler(fehlt)
    setFehler(null)
    setLaeuft(true)
    try {
      const r = await api.registrieren(bestellungskoerper(), uuidv7())
      aufAngemeldet(r.session as SessionUser, paket)
    } catch (e) {
      setFehler(fehlerText(e))
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Der Betrag für dieses Paket und Intervall – oder gar keiner.
   *
   * Er kommt aus dem Stripe-Konto. Fehlt er, gibt diese Funktion eine leere
   * Zeichenkette zurück, und die Oberfläche schreibt «monatlich» statt einer
   * Zahl. Das ist die ehrliche Lücke: Der verbindliche Betrag steht auf der
   * Stripe-Seite, die als Nächstes kommt.
   */
  function betragText(fuer: 'monat' | 'jahr'): string {
    const rp = preise.get(`${paket}:${fuer}`)
    if (rp === undefined || rp === null) {
      /* Der Monatspreis steht auch in `pakete.ts` – als Rückfall gültig,
         solange Stripe keine abweichende Zahl geliefert hat. */
      return fuer === 'monat' ? preisText(paketVon(paket)?.preisRp ?? 0) : ''
    }
    return preisText(rp)
  }

  const gewaehlt = paketVon(paket)

  return (
    <div className="rp-public rp-bestellung">
      <header className="rp-best-kopf">
        <button type="button" className="rp-marke" onClick={aufStart} id="btn-marke">
          <Marke groesse={26} />
        </button>
        <button type="button" className="rp-textknopf" id="btn-best-login" onClick={aufLogin}>
          Ich habe schon einen Zugang
        </button>
      </header>

      <main className="rp-best-blatt">
        {/*
          Der Fortschritt steht oben und ist nicht anklickbar.

          Drei Punkte, einer hell. Wer sieht, dass es drei sind, füllt das
          Formular zu Ende; wer es nicht sieht, bricht beim zweiten ab, weil er
          nicht weiss, ob noch zehn kommen.
        */}
        <ol className="rp-best-schritte" aria-label="Fortschritt">
          {(
            [
              ['paket', 'Paket'],
              ['betrieb', 'Betrieb'],
              ['zugang', 'Zugang'],
              ['zahlung', 'Zahlung']
            ] as Array<[Schritt, string]>
          ).map(([id, wort], i) => (
            <li key={id} className={id === schritt ? 'aktiv' : undefined}>
              <span>{i + 1}</span>
              {wort}
            </li>
          ))}
        </ol>

        {fehler && (
          <div className="rp-best-fehler" id="bestellung-fehler">
            {fehler}
          </div>
        )}

        {/*
          Wer eingeladen wurde, soll über jedem Schritt sehen, wohin das
          führt. Sonst steht sie vor einem allgemeinen Bestellformular und
          fragt sich, ob die Einladung noch gilt.
        */}
        {kita && (
          <div className="rp-best-suche" id="best-einladung">
            <label>Einladung von {kita.kita}</label>
            <span className="rp-best-hinweis">
              Dein Bereich gehört danach zu <strong>{kita.kita}</strong>
              {kita.ort ? ` in ${kita.ort}` : ''}. Kita-Name, Logo und Adresse im Briefkopf
              kommen von dort und werden dort gepflegt. Deine Firma, deine UID und deine
              Bankverbindung bleiben deine — die trägst du gleich selbst ein.
            </span>
          </div>
        )}

        {/* ============================ 1 · Paket ============================ */}
        {schritt === 'paket' && (
          <section className="rp-best-teil" id="best-paket">
            <h1>Welches Paket?</h1>
            <p className="rp-best-unter">
              {testTage} Tage kostenlos, jederzeit kündbar. Das Paket lässt sich später wechseln.
            </p>

            <div className="rp-best-pakete">
              {PAKETE.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  id={`best-paket-${p.id}`}
                  className={p.id === paket ? 'rp-best-paket gewaehlt' : 'rp-best-paket'}
                  aria-pressed={p.id === paket}
                  onClick={() => setPaket(p.id)}
                >
                  <b>{p.name}</b>
                  <i>{p.fuerWen}</i>
                  <strong>
                    {preisText(p.preisRp)} <span>CHF / Monat</span>
                    <small className="rp-best-steuer">{STEUERHINWEIS}</small>
                  </strong>
                  <em>{p.kern}</em>
                </button>
              ))}
            </div>

            {/*
              Nach einer Einladung ist die Branche keine Frage mehr.

              Eine Kita lädt ein; etwas anderes als Kinderbetreuung ergibt
              dort keinen Sinn. Und die falsche Antwort kostet genau das,
              wofür sie kommt: Die Betreuungsoberfläche — Kinder, Eltern,
              Betreuungsverträge, Monatslauf — hängt am Branchenwert, nicht
              an der Gemeinschaft. Wer hier «Andere» stehen lässt, bekommt
              eine Gemeinschaft ohne Betreuung und merkt es erst danach.
            */}
            <div className="rp-best-feld">
              <label htmlFor="best-branche">Was macht dein Betrieb?</label>
              {kita ? (
                <>
                  <input
                    id="best-branche"
                    readOnly
                    value={INDUSTRY_LABELS.kinderbetreuung}
                    aria-describedby="best-branche-fest"
                  />
                  <span className="rp-best-hinweis" id="best-branche-fest">
                    Steht fest, weil dich eine Kita eingeladen hat. Daran hängen Kinder,
                    Eltern und der Monatslauf.
                  </span>
                </>
              ) : (
                <>
                  <select
                    id="best-branche"
                    value={branche}
                    onChange={(e) => setBranche(e.target.value as Industry)}
                  >
                    {INDUSTRIES.map((b) => (
                      <option key={b} value={b}>
                        {INDUSTRY_LABELS[b]}
                      </option>
                    ))}
                  </select>
                  {/*
                    Der Satz erklärt, wozu die Frage dient, und er ändert sich
                    mit der Antwort.

                    Ohne ihn ist die Frage eine Datenabfrage, die man ungern
                    beantwortet. Mit ihm ist sie eine Abkürzung – und wer
                    «Keine davon passt» wählt, erfährt sofort, dass er mit
                    einem leeren Katalog anfängt, statt es eine Stunde später
                    zu merken.
                  */}
                  <span className="rp-best-hinweis">
                    {mitKatalog.has(branche) ? (
                      <>
                        Danach steht ein Grundkatalog für {INDUSTRY_LABELS[branche]} bereit —
                        Leistungen und Einheiten, schon eingetragen. Die Preise setzt du selbst;
                        wir raten sie nicht.
                      </>
                    ) : (
                      <>
                        Für diese Branche gibt es noch keinen Grundkatalog — du fängst mit einem
                        leeren an und trägst deine Leistungen selbst ein. Einen zu erfinden, den
                        wir nicht kennen, wäre schlechter als keiner.
                      </>
                    )}
                  </span>
                </>
              )}
            </div>

            {/*
              Der Einladungscode steht hier, im ersten Schritt.

              Er ist die Tür, nicht die letzte Formalität: wer keinen hat, soll
              es lesen, bevor er Firma, Adresse und Passwort eintippt. Der Satz
              daneben sagt auch, wie man an einen kommt – sonst steht der
              Besucher vor einem Feld, das er nicht füllen kann, und geht.
            */}
            {codeNoetig && (
              <div className="rp-best-feld">
                <label htmlFor="best-code">Einladungscode</label>
                <input
                  id="best-code"
                  autoComplete="off"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <span className="rp-best-hinweis">
                  Raporto ist zurzeit für eingeladene Betriebe offen. Keinen Code? Schreib an{' '}
                  <a href="mailto:info@raporto.ch">info@raporto.ch</a>.
                </span>
              </div>
            )}

            <button
              type="button"
              className="rp-knopf rp-knopf-gold rp-knopf-gross"
              id="btn-best-weiter-1"
              disabled={kitaLaeuft || (codeNoetig && code.trim() === '')}
              onClick={() => setSchritt('betrieb')}
            >
              {kitaLaeuft ? 'Einen Moment …' : 'Weiter'}
            </button>
          </section>
        )}

        {/* =========================== 2 · Betrieb =========================== */}
        {schritt === 'betrieb' && (
          <section className="rp-best-teil" id="best-betrieb">
            <h1>Dein Betrieb</h1>
            <p className="rp-best-unter">
              Diese Angaben stehen später auf jeder Rechnung und jeder Offerte. Ändern kannst du sie
              jederzeit.
            </p>

            {handelsregister && (
              <div className="rp-best-suche">
                <label htmlFor="best-suche">Im Handelsregister suchen</label>
                <div className="rp-best-suchzeile">
                  <input
                    autoComplete="off"
                    id="best-suche"
                    value={suchtext}
                    placeholder="Firmenname"
                    onChange={(e) => setSuchtext(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        void suchen()
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="rp-knopf"
                    id="btn-best-suchen"
                    disabled={sucht || suchtext.trim().length < 3}
                    onClick={() => void suchen()}
                  >
                    {sucht ? 'Sucht …' : 'Suchen'}
                  </button>
                </div>

                {treffer !== null && treffer.length === 0 && !sucht && (
                  <p className="rp-best-hinweis">
                    Nichts gefunden — trag die Angaben von Hand ein.
                  </p>
                )}

                {treffer !== null && treffer.length > 0 && (
                  <ul className="rp-best-treffer">
                    {treffer.map((t) => (
                      <li key={t.uid}>
                        <button type="button" onClick={() => uebernehmen(t)}>
                          <b>{t.name}</b>
                          <i>
                            {t.rechtsform} · {t.sitz} · {t.uid}
                          </i>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="rp-best-feld">
              <label htmlFor="best-firma">Firmenname</label>
              <input
                autoComplete="off"
                id="best-firma"
                value={firma.name}
                onChange={(e) => setFirma({ ...firma, name: e.target.value })}
              />
            </div>

            {/*
              Firmenmail und Firmentelefon – die Angaben des BETRIEBS.

              Sie wurden hier bisher nicht gefragt, und deshalb blieb der
              Briefkopf bei fast jedem Betrieb ohne Kontaktangaben: die Felder
              gibt es in den Einstellungen, aber wer sucht dort nach etwas, von
              dem er nicht weiss, dass es fehlt?

              Die Beschriftung nennt ausdrücklich den Unterschied zur Adresse
              der Person weiter unten. «info@…» und «max@…» sind zwei
              verschiedene Dinge, und nur das zweite ist der Login.
            */}
            <div className="rp-best-zwei">
              <div className="rp-best-feld">
                <label htmlFor="best-firma-mail">Firmen-E-Mail (optional)</label>
                <input
                  autoComplete="off"
                  id="best-firma-mail"
                  type="email"
                  value={firma.email}
                  placeholder="info@deinbetrieb.ch"
                  onChange={(e) => setFirma({ ...firma, email: e.target.value })}
                />
                <span className="rp-best-hinweis">
                  Diese Adresse verwenden wir als geschäftliche Kontaktadresse. Sie ist nicht
                  deine Anmeldung.
                </span>
              </div>
              <div className="rp-best-feld">
                <label htmlFor="best-firma-tel">Firmentelefon</label>
                <input
                  autoComplete="off"
                  id="best-firma-tel"
                  value={firma.phone}
                  placeholder="044 000 00 00"
                  onChange={(e) => setFirma({ ...firma, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="rp-best-zwei">
              <div className="rp-best-feld">
                <label htmlFor="best-strasse">Strasse und Nummer</label>
                <input
                  autoComplete="off"
                  id="best-strasse"
                  value={firma.street}
                  onChange={(e) => setFirma({ ...firma, street: e.target.value })}
                />
              </div>
              <div className="rp-best-feld">
                <label htmlFor="best-uid">UID</label>
                <input
                  autoComplete="off"
                  id="best-uid"
                  value={firma.uid}
                  placeholder="CHE-000.000.000"
                  onChange={(e) => setFirma({ ...firma, uid: e.target.value })}
                />
              </div>
            </div>

            {/*
              Dieselbe Ortssuche wie im Büro, nur ohne eigenen Bestand – den
              hat ein Betrieb hier noch nicht. Das Feldpaar stand vorher als
              zwei nackte Eingaben da, und ausgerechnet beim ersten Eindruck
              musste der Ort von Hand getippt werden.
            */}
            <OrtsFeldPaar
              praefix="best"
              plz={firma.zip}
              ort={firma.city}
              aufAenderung={({ plz, ort }) => setFirma({ ...firma, zip: plz, city: ort })}
              ortschaften={OHNE_BESTAND}
              klassen={{ reihe: 'rp-best-zwei', feld: 'rp-best-feld' }}
            />

            {/* ---------------------- Nummernkreis ---------------------- */}
            <div className="rp-best-kreis">
              <label className="rp-best-check">
                <input
                  autoComplete="off"
                  type="checkbox"
                  id="best-umzug"
                  checked={umzug}
                  onChange={(e) => setUmzug(e.target.checked)}
                />
                <span>Ich komme aus einer anderen Software und führe meine Nummern weiter</span>
              </label>

              {umzug && (
                <>
                  <div className="rp-best-zwei">
                    <div className="rp-best-feld">
                      <label htmlFor="best-next-re">Nächste Rechnungsnummer</label>
                      <input
                        autoComplete="off"
                        id="best-next-re"
                        inputMode="numeric"
                        value={weiterRechnung}
                        onChange={(e) =>
                          setWeiterRechnung(
                            Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1)
                          )
                        }
                      />
                    </div>
                    <div className="rp-best-feld">
                      <label htmlFor="best-next-of">Nächste Offertnummer</label>
                      <input
                        autoComplete="off"
                        id="best-next-of"
                        inputMode="numeric"
                        value={weiterOfferte}
                        onChange={(e) =>
                          setWeiterOfferte(
                            Math.max(1, Number(e.target.value.replace(/\D/g, '')) || 1)
                          )
                        }
                      />
                    </div>
                  </div>
                  <p className="rp-best-hinweis">
                    Trage ein, was in deiner alten Software als nächste Nummer käme. Zurückdrehen
                    lässt sich der Zähler später nicht – eine Nummer zweimal zu vergeben ist der
                    eine Fehler, den weder du noch dein Treuhänder geradebiegen kann.
                  </p>
                </>
              )}

              {/*
                Der Jahreswechsel steht hier ausgeschrieben und nicht als
                Fachwort. Was am 1. Januar passiert, soll niemand erst
                herausfinden, wenn es passiert ist.
              */}
              <label className="rp-best-check">
                <input
                  autoComplete="off"
                  type="checkbox"
                  id="best-jaehrlich"
                  checked={jaehrlich}
                  onChange={(e) => setJaehrlich(e.target.checked)}
                />
                <span>Jedes Jahr neu bei 1 beginnen</span>
              </label>
              <p className="rp-best-hinweis" id="best-jahr-satz">
                {jaehrlich
                  ? `Am 1. Januar ${new Date().getFullYear() + 1} beginnt der Zähler von selbst wieder bei 1, und die Jahreszahl in der Nummer wechselt mit. Das machen wir automatisch, solange du hier nichts änderst.`
                  : `Der Zähler läuft über den Jahreswechsel durch. Die Jahreszahl in der Nummer wechselt trotzdem auf ${new Date().getFullYear() + 1}.`}
              </p>
            </div>

            <div className="rp-best-fuss">
              <button
                type="button"
                className="rp-textknopf"
                id="btn-best-zurueck-2"
                onClick={() => setSchritt('paket')}
              >
                Zurück
              </button>
              <button
                type="button"
                className="rp-knopf rp-knopf-gold rp-knopf-gross"
                id="btn-best-weiter-2"
                disabled={!firma.name.trim()}
                onClick={() => setSchritt('zugang')}
              >
                Weiter
              </button>
            </div>
          </section>
        )}

        {/* ============================ 3 · Zugang =========================== */}
        {schritt === 'zugang' && (
          <section className="rp-best-teil" id="best-zugang">
            <h1>Dein Zugang</h1>
            <p className="rp-best-unter">
              Deine eigene Adresse, nicht die der Firma. Mit ihr meldest du dich bei Raporto an;
              an ihr hängt später auch der zweite Faktor. Mitarbeiter bekommen jeder ihren eigenen
              Zugang.
            </p>

            {vomBrowser.length > 0 && (
              <div className="rp-best-fehler" id="best-autofill-warnung">
                <strong>Dein Browser hat ausgefüllt: {vomBrowser.join(', ')}.</strong> Diese Werte
                kommen aus dem Adressbuch deines Browsers, nicht aus Raporto. Prüfe besonders die
                E-Mail-Adresse – sie wird zur Anmeldung dieses Betriebs und lässt sich danach nicht
                mehr einfach wechseln.
              </div>
            )}

            <div className="rp-best-feld">
              <label htmlFor="best-person">Dein Name</label>
              <input
                autoComplete="off"
                id="best-person"
                value={person.name}
                onChange={(e) => setPerson({ ...person, name: e.target.value })}
              />
            </div>

            <div className="rp-best-zwei">
              <div className="rp-best-feld">
                <label htmlFor="best-mail">Deine Anmelde-E-Mail</label>
                <input
                  id="best-mail"
                  /*
                    Kein type="email" und kein Autofill.

                    Beides zusammen macht das Feld fuer Chrome zu einem
                    Adressfeld, und dann bietet er Adressen aus seinem
                    Adressbuch an - auch solche, die zu einem ganz anderen
                    Betrieb gehoeren. Hier ist das folgenreicher als anderswo:
                    die Adresse, die in diesem Feld landet, BESITZT danach den
                    Betrieb. Sie traegt die Anmeldung und den zweiten Faktor.

                    inputMode gibt dem Handy weiterhin die richtige Tastatur.
                  */
                  inputMode="email"
                  autoComplete="off"
                  value={person.email}
                  onChange={(e) => setPerson({ ...person, email: e.target.value })}
                />
                <span className="rp-best-hinweis">
                  Mit dieser E-Mail und deinem Passwort meldest du dich künftig bei Raporto an.
                </span>
              </div>
              <div className="rp-best-feld">
                <label htmlFor="best-telefon">Deine Telefonnummer</label>
                <input
                  autoComplete="off"
                  id="best-telefon"
                  inputMode="tel"
                  value={person.phone}
                  placeholder="+41 …"
                  onChange={(e) => setPerson({ ...person, phone: e.target.value })}
                />
              </div>
            </div>

            {/*
              Die Begründung steht dabei, nicht nur die Regel. «Mindestens 12
              Zeichen» klingt nach Schikane; der Satz daneben sagt, was an
              diesem Konto hängt. Die Regel selbst – und der Vorschlagsknopf –
              kommen aus `ui/Passwortfeld`, damit hier nicht eine vierte
              Fassung derselben Prüfung entsteht.
            */}
            <Passwortfeld
              id="best-passwort"
              stil="bestellung"
              label="Passwort"
              wert={person.passwort}
              aufWert={(w) => setPerson({ ...person, passwort: w })}
              umfeld={{ email: person.email, name: person.name, betrieb: firma.name }}
            />
            <span className="rp-best-hinweis">
              An diesem Zugang hängen später deine Bankverbindung, dein Kundenstamm und jede
              Rechnung, die du schreibst.
            </span>

            <div className="rp-best-zusammenfassung">
              <div>
                <span>Paket</span>
                <b>
                  {gewaehlt?.name} · {preisText(gewaehlt?.preisRp ?? 0)} CHF / Monat,{' '}
                  {STEUERHINWEIS}
                </b>
              </div>
              <div>
                <span>Testphase</span>
                <b>{testTage} Tage kostenlos, danach monatlich · jederzeit kündbar</b>
              </div>
              <div>
                <span>Betrieb</span>
                <b>{firma.name}</b>
              </div>
              {/*
                Die Anmeldeadresse steht noch einmal hier, direkt ueber dem
                Knopf, der sie verbindlich macht. Wer im Feld darueber eine
                falsche uebernommen hat, sieht sie spaetestens jetzt - und
                daneben steht ein Zurueck.
              */}
              <div>
                <span>Deine Anmeldung</span>
                <b>{person.email || 'noch keine Adresse'}</b>
              </div>
            </div>

            <div className="rp-best-fuss">
              <button
                type="button"
                className="rp-textknopf"
                id="btn-best-zurueck-3"
                onClick={() => setSchritt('betrieb')}
              >
                Zurück
              </button>
              <button
                type="button"
                className="rp-knopf rp-knopf-gold rp-knopf-gross"
                id="btn-best-weiter-3"
                onClick={() => {
                  const fehlt = wasFehlt()
                  if (fehlt) return setFehler(fehlt)
                  setFehler(null)
                  setSchritt('zahlung')
                }}
              >
                Weiter
              </button>
            </div>
          </section>
        )}

        {/* =========================== 4 · Zahlung =========================== */}
        {schritt === 'zahlung' && (
          <section className="rp-best-teil" id="best-zahlung">
            <h1>Testphase starten</h1>
            <p className="rp-best-unter">
              {testTage} Tage kostenlos. Danach läuft das Abo weiter, solange du nicht kündigst.
            </p>

            {/*
              Monatlich oder jährlich.

              Zwei Knöpfe und kein Auswahlfeld: Es sind zwei Möglichkeiten, und
              beide sollen sichtbar sein, samt Preis. Ein zugeklapptes Feld
              versteckt genau die Information, wegen der man wählt.
            */}
            <div className="rp-best-intervall" role="group" aria-label="Abrechnung">
              {(
                [
                  ['monat', 'Monatlich', 'pro Monat'],
                  ['jahr', 'Jährlich', 'pro Jahr']
                ] as Array<['monat' | 'jahr', string, string]>
              ).map(([id, wort, je]) => (
                <button
                  key={id}
                  type="button"
                  id={`best-intervall-${id}`}
                  className={intervall === id ? 'aktiv' : undefined}
                  aria-pressed={intervall === id}
                  onClick={() => setIntervall(id)}
                >
                  <b>{wort}</b>
                  <span>{betragText(id) ? `${betragText(id)} CHF ${je}` : je}</span>
                </button>
              ))}
            </div>

            <div className="rp-best-zusammenfassung" id="best-uebersicht">
              <div>
                <span>Paket</span>
                <b>
                  {gewaehlt?.name}
                  {betragText(intervall)
                    ? ` · ${betragText(intervall)} CHF / ${intervall === 'monat' ? 'Monat' : 'Jahr'}, ${STEUERHINWEIS}`
                    : ` · ${intervall === 'monat' ? 'monatlich' : 'jährlich'}, ${STEUERHINWEIS}`}
                </b>
              </div>
              <div>
                <span>Betrieb</span>
                <b>{firma.name}</b>
              </div>
              <div>
                <span>Deine Anmeldung</span>
                <b>{person.email || 'noch keine Adresse'}</b>
              </div>
              <div>
                <span>Testphase</span>
                <b>{testTage} Tage · jederzeit kündbar</b>
              </div>
            </div>

            {/*
              Der Hinweis, auf den es ankommt.

              Er steht UNMITTELBAR über dem Knopf und nicht als Fussnote, denn
              er beantwortet die eine Frage, die in diesem Moment jeder hat:
              «Zieht ihr mir jetzt Geld ab?» Die Antwort ist nein, und sie
              steht wörtlich da.
            */}
            <div className="rp-best-zusage" id="best-zahlungshinweis">
              <ul>
                {ZAHLUNGSZUSAGE.map((satz) => (
                  <li key={satz}>{satz}</li>
                ))}
              </ul>
              {zahlung.sandbox && (
                <p className="rp-best-sandbox" id="best-sandbox">
                  Testmodus – es fliesst kein echtes Geld.
                </p>
              )}
            </div>

            <div className="rp-best-fuss">
              <button
                type="button"
                className="rp-textknopf"
                id="btn-best-zurueck-4"
                onClick={() => setSchritt('zugang')}
              >
                Zurück
              </button>
              {zahlung.verfuegbar ? (
                <button
                  type="button"
                  className="rp-knopf rp-knopf-gold rp-knopf-gross"
                  id="btn-best-zahlung"
                  disabled={laeuft}
                  onClick={() => void zurZahlung()}
                >
                  {laeuft ? 'Wird geöffnet …' : 'Weiter zur Zahlung'}
                </button>
              ) : (
                /*
                  Ohne eingerichtete Zahlung gibt es keinen Checkout – und
                  dann sagt der Knopf das, was er tut. Ein «Weiter zur
                  Zahlung», das zu keiner Zahlung führt, wäre die Unwahrheit
                  direkt neben der Zusage darüber.
                */
                <button
                  type="button"
                  className="rp-knopf rp-knopf-gold rp-knopf-gross"
                  id="btn-best-absenden"
                  disabled={laeuft}
                  onClick={() => void bestellen()}
                >
                  {laeuft ? 'Wird eingerichtet …' : 'Betrieb einrichten'}
                </button>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
